<template>
  <div class="flex gap-2">
    <template v-for="tag in inlineTags" :key="tag">
      <Component
        :is="tag.link ? 'a' : 'div'"
        :href="tag.link"
        :class="[size, variant, tag.link && 'hover:opacity-80']"
        class="tag"
      >
        {{ tag.label }}
      </Component>
    </template>

    <IAITooltip v-if="rest.length" side="bottom" :side-offset="5">
      <template #trigger>
        <div class="tag" :class="[size, variant]">{{ `+${rest.length}` }}</div>
      </template>

      <template #content>
        <div>
          <div class="tooltip-list flex max-w-[200px] flex-wrap gap-2 px-1 py-2">
            <template v-for="tag in rest" :key="tag">
              <Component :is="tag.link ? 'a' : 'div'" :href="tag.link" class="tag" :class="[variant]">
                {{ tag.label }}
              </Component>
            </template>
          </div>
        </div>
      </template>
    </IAITooltip>
  </div>
</template>

<script setup lang="ts">
import { IAITooltip } from "@/core-ui-vue";
import { computed } from "vue";

const MAX_INLINE_LENGTH = 20;
type TagSize = "small" | "large";
type ColorVariant = "default" | "pink" | "green";

interface Tag {
  label: string;
  link?: string;
}

const props = withDefaults(
  defineProps<{
    tagList: Tag[];
    showAll: boolean;
    size: TagSize;
    variant?: ColorVariant;
  }>(),
  {
    tagList: () => [],
    showAll: true,
    size: "large",
    variant: "default",
  }
);

const inlineTagsNumber = computed(() => {
  const twoFirstTags = props.tagList.slice(0, 2);
  const twoFirstTagsLength = twoFirstTags.reduce((acc, tag) => {
    return acc + tag.label.length;
  }, 0);

  if (twoFirstTagsLength >= MAX_INLINE_LENGTH) {
    return 1;
  }

  return 2;
});

const endSliceLimit = computed(() => (props.showAll ? props.tagList.length : inlineTagsNumber.value));
const inlineTags = computed(() => props.tagList.slice(0, endSliceLimit.value));
const rest = computed(() => props.tagList.slice(endSliceLimit.value));
</script>

<style scoped lang="scss">
.tag {
  @apply whitespace-nowrap rounded-[4px] px-2 py-0.5 text-xs text-theme-1;
  text-decoration: none;

  &.large {
    @apply rounded-md px-5 py-2 text-sm;
  }

  &.default {
    @apply bg-theme-placeholder-bg;
  }

  &.pink {
    @apply bg-theme-3 text-white;
  }

  &.green {
    @apply bg-theme-success-b;
  }
}
</style>
